import { useI18n } from "@/contexts/I18nContext";
import {
  ProForm,
  ProFormSelect,
  ProFormText,
  ProFormTextArea,
} from "@ant-design/pro-components";
import { Button, Form } from "antd";
import { memo, useRef, useState } from "react";
import { formatFiles, generateUuid, getAgentFromScript } from "@/utils";
import { getProductList, submitTicket } from "@/services";
import { useWebSocket } from "../WebSocketProvider";
import toast from "react-hot-toast";
import FileUploadButton from "../FileUploadButton";

interface SubmitFormProps {
  setDialogueMessages: any;
  setTicketUrl?: any;
}

/**
 * 提交表单
 * @returns
 */
const SubmitForm = ({ setDialogueMessages, setTicketUrl }: SubmitFormProps) => {
  const [form] = Form.useForm();
  const tickForm = useRef<HTMLDivElement | null>(null);
  const { formatMessage } = useI18n();
  const { lastMessage } = useWebSocket();
  const [loading, setLoading] = useState(false);

  return (
    <div ref={tickForm}>
      <ProForm
        form={form}
        layout="vertical"
        submitter={{
          resetButtonProps: false,
          searchConfig: {
            submitText: formatMessage("submitText"),
          },
        }}
        loading={loading}
        onFinish={async (values) => {
          const messageId =
            lastMessage?.data?.messages?.[0]?.id || generateUuid();
          try {
            setLoading(true);
            const res: any = await submitTicket({
              agent: getAgentFromScript(),
              messageId: messageId,
              ...values,
            });
            if (res?.code === 0) {
              form.resetFields();
              setDialogueMessages((prev: any) => {
                const newMessage = [...prev];
                const lastIndex = newMessage.length - 1;
                newMessage[lastIndex] = {
                  ...newMessage[lastIndex],
                  hasTickFormSuccess: true, //标识表单提交成功
                };
                return newMessage;
              });
              //设置表单链接跳转地址
              if (setTicketUrl) {
                setTicketUrl(res?.data?.ticket_url);
              }
            }
          } catch (error) {
            console.error(error);
            toast.success(formatMessage("submitField"));
          } finally {
            setLoading(false);
          }
        }}
      >
        <ProFormText
          name="email"
          label={formatMessage("emailLabel")}
          placeholder={formatMessage("emailPlaceholder")}
          rules={[
            {
              required: true,
              message: "",
            },
            {
              type: "email",
              message: formatMessage("emailErrorText"),
            },
          ]}
        />
        <ProFormSelect
          name="pid"
          label={formatMessage("productLabel")}
          placeholder={formatMessage("productPlaceholder")}
          rules={[
            {
              required: true,
              message: "",
            },
          ]}
          fieldProps={{
            showSearch: true,
            fetchDataOnSearch: false,
            fieldNames: {
              label: "product_name",
              value: "pid",
            },
            getPopupContainer: () => tickForm?.current || document.body,
          }}
          request={async () => {
            const { data } = await getProductList({
              agent: getAgentFromScript(),
            });
            return data || [];
          }}
        />
        <ProFormTextArea
          name="description"
          label={formatMessage("problemLabel")}
          placeholder={formatMessage("problemPlaceholder")}
        />
        <ProForm.Item name="attachments">
          <FileUploadButton
            formatMessage={formatMessage}
            onFileChange={(files) => {
              const resultFile: any = formatFiles(files);
              form.setFieldsValue({ attachments: [resultFile] });
            }}
          >
            <Button type="link">{formatMessage("uploadAttachment")}</Button>
          </FileUploadButton>
        </ProForm.Item>
      </ProForm>
    </div>
  );
};

export default memo(SubmitForm);
